import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users, Search, Shield, User, Baby, Heart, Phone, Crown, X, AlertCircle, RefreshCw, Home, Eye, Plus, Link2, Unlink, UserPlus, ChevronRight, Check } from 'lucide-react';
import { familyAPI, patientAPI } from '../services/api';
import useDialog from '../hooks/useDialog';

const FamilyManagement = ({ hasPermission }) => {
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('FAMILY', action) : true),
    [hasPermission],
  );

  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [familyStats, setFamilyStats] = useState({ totalFamilies: 0, totalMembers: 0, adultsAsHeads: 0, childrenWithGuardians: 0, avgFamilySize: 0, patientsWithoutFamily: 0 });
  // Unlinked patient results from main search
  const [unlinkedPatients, setUnlinkedPatients] = useState([]);
  const [searchPerformed, setSearchPerformed] = useState(false);
  // The term the families on screen were found with; '' while nothing has been searched.
  const [searchedTerm, setSearchedTerm] = useState('');

  // Link member flow
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkStep, setLinkStep] = useState('search-head'); // 'search-head', 'search-member', 'confirm'
  const [selectedHead, setSelectedHead] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  // Why an identifier search found nobody, when the server could tell us.
  const [patientSearchHint, setPatientSearchHint] = useState('');
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [linkingInProgress, setLinkingInProgress] = useState(false);

  // Unlink flow
  const [unlinkConfirm, setUnlinkConfirm] = useState(null);

  // Scrollable body of the multi-step Link wizard — reset its scroll on each step change
  // so a long results list on one step doesn't leave the next step scrolled down.
  const linkModalBodyRef = useRef(null);
  useEffect(() => {
    if (linkModalBodyRef.current) linkModalBodyRef.current.scrollTop = 0;
  }, [linkStep]);

  // Families are listed only once a search asks for them. Listing every family on arrival put each
  // head's name, CNIC and phone on screen for anyone at the desk: the confidentiality risk the
  // owner raised against the Patients page (2026-09-29) applies here too.
  useEffect(() => { loadFamilyStats(); }, []);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  const clearFamilies = () => {
    setSearchedTerm(''); setSearchPerformed(false); setFamilies([]); setUnlinkedPatients([]); setError(null);
  };

  // Brings the list up to date after a change: the same search again, or still nothing.
  const refreshFamilies = () => (searchedTerm ? runSearch(searchedTerm) : clearFamilies());

  const loadFamilyStats = async () => {
    try {
      const stats = await familyAPI.getStats();
      setFamilyStats({ totalFamilies: stats.totalFamilies || 0, totalMembers: stats.totalMembers || 0, adultsAsHeads: stats.adultsAsHeads || 0, childrenWithGuardians: stats.childrenWithGuardians || 0, avgFamilySize: stats.avgFamilySize || 0, patientsWithoutFamily: stats.patientsWithoutFamily || 0 });
    } catch (err) { console.error('Error loading stats:', err); }
  };

  const searchFamilies = () => {
    const term = searchTerm.trim();
    if (!term) { clearFamilies(); return; }
    runSearch(term);
  };

  const runSearch = async (term) => {
    setLoading(true); setError(null); setSearchPerformed(true); setSearchedTerm(term);
    try {
      // Search both families and individual patients in parallel
      const [familyResults, patientResult] = await Promise.all([
        familyAPI.search(term).catch(() => []),
        patientAPI.search(term).catch(() => ({ content: [] }))
      ]);
      const foundFamilies = familyResults || [];
      setFamilies(foundFamilies);
      // Find patients not already shown as family heads/members
      const familyPatientIds = new Set();
      foundFamilies.forEach(f => {
        if (f.familyHead?.id) familyPatientIds.add(f.familyHead.id);
        (f.members || []).forEach(m => { if (m.id) familyPatientIds.add(m.id); });
      });
      const allPatients = patientResult?.content || patientResult || [];
      const unlinked = (Array.isArray(allPatients) ? allPatients : []).filter(p => !familyPatientIds.has(p.id));
      setUnlinkedPatients(unlinked);
    } catch (err) { setError('Search failed.'); setFamilies([]); setUnlinkedPatients([]); } finally { setLoading(false); }
  };

  const searchPatients = useCallback(async (query) => {
    if (!query || !query.trim()) { setPatientResults([]); setPatientSearchHint(''); return; }
    setSearchingPatients(true);
    try {
      const result = await patientAPI.search(query);
      const patients = result?.content || result || [];
      setPatientResults(Array.isArray(patients) ? patients : []);
      setPatientSearchHint(result?.searchHint || '');
    } catch (err) {
      console.error('Patient search error:', err);
      setPatientResults([]);
      setPatientSearchHint('');
    } finally { setSearchingPatients(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { searchPatients(patientSearch); }, 300);
    return () => clearTimeout(timer);
  }, [patientSearch, searchPatients]);

  const openLinkModal = (preselectedHead = null) => {
    setShowLinkModal(true);
    setPatientSearch('');
    setPatientResults([]);
    setSelectedMember(null);
    if (preselectedHead) {
      setSelectedHead(preselectedHead);
      setLinkStep('search-member');
    } else {
      setSelectedHead(null);
      setLinkStep('search-head');
    }
  };

  const closeLinkModal = () => {
    setShowLinkModal(false);
    setSelectedHead(null);
    setSelectedMember(null);
    setPatientSearch('');
    setPatientResults([]);
    setLinkStep('search-head');
  };

  const familyDetailsDialogRef = useDialog(() => setSelectedFamily(null), { confirmDiscard: false });
  const linkDialogRef = useDialog(closeLinkModal, { confirmDiscard: false });
  const unlinkDialogRef = useDialog(() => setUnlinkConfirm(null), { confirmDiscard: false });

  const selectAsHead = (patient) => {
    setSelectedHead(patient);
    setLinkStep('search-member');
    setPatientSearch('');
    setPatientResults([]);
  };

  const selectAsMember = (patient) => {
    setSelectedMember(patient);
    setLinkStep('confirm');
  };

  const confirmLink = async () => {
    if (!selectedHead || !selectedMember) return;
    setLinkingInProgress(true);
    try {
      await familyAPI.linkFamilyMembers(selectedHead.id, selectedMember.id);
      showSuccess(`${selectedMember.fullName || (selectedMember.firstName + ' ' + selectedMember.lastName)} linked to ${selectedHead.fullName || (selectedHead.firstName + ' ' + selectedHead.lastName)}'s family`);
      closeLinkModal();
      refreshFamilies();
      loadFamilyStats();
    } catch (err) {
      const msg = err?.message || err?.error || 'Failed to link family members';
      setError(typeof msg === 'string' ? msg : 'Failed to link family members');
    } finally { setLinkingInProgress(false); }
  };

  const confirmUnlink = async () => {
    if (!unlinkConfirm) return;
    const { familyHeadId, memberId, memberName } = unlinkConfirm;
    try {
      await familyAPI.unlinkFamilyMember(familyHeadId, memberId);
      showSuccess(`${memberName} has been unlinked from the family`);
      setUnlinkConfirm(null);
      // Refresh the open family: found again by its head's MRN, never by loading every family. A
      // family left with no members is no longer one, and its detail closes.
      const headMrn = selectedFamily?.familyHead?.mrn;
      const found = headMrn ? (await familyAPI.search(headMrn).catch(() => [])) || [] : [];
      setSelectedFamily(found.find(f => f.id === selectedFamily?.id) || null);
      refreshFamilies();
      loadFamilyStats();
    } catch (err) {
      setError('Failed to unlink family member');
    }
  };

  const getRelationshipColor = (rel, age) => age < 18 ? 'bg-amber-100 text-amber-800' : rel?.toLowerCase() === 'wife' ? 'bg-pink-100 text-pink-800' : 'bg-blue-100 text-blue-800';

  const getPatientName = (p) => p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim();
  const getPatientAge = (p) => p.age != null ? p.age : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center"><Users className="h-7 w-7 text-white" /></div>
            <div><h1 className="text-3xl font-bold text-gray-900">Family Management</h1><p className="text-gray-600">Manage patient family relationships</p></div>
          </div>
          <div className="flex items-center space-x-3">
            {can('LINK_MEMBER') && (
              <button onClick={() => openLinkModal()} className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <Link2 className="h-5 w-5" /><span>Link Family Members</span>
              </button>
            )}
            <button onClick={() => { refreshFamilies(); loadFamilyStats(); }} disabled={loading} className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} /><span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Success / Error Messages */}
        {successMsg && <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 mb-6 rounded-lg flex items-center"><Check className="h-5 w-5 text-emerald-600 mr-2" /><p className="text-emerald-700">{successMsg}</p></div>}
        {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg flex items-center justify-between"><div className="flex items-center"><AlertCircle className="h-5 w-5 text-red-500 mr-2" /><p className="text-red-700">{error}</p></div><button aria-label="Dismiss" onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button></div>}

        {/* Search */}
        <form onSubmit={(e) => { e.preventDefault(); searchFamilies(); }} className="bg-white rounded-2xl shadow-xl p-6 mb-6 flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input type="text" placeholder="Search families by name, CNIC, MRN, phone..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
          </div>
          <button type="submit" disabled={loading} className="px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50">Search</button>
          {searchTerm && <button type="button" onClick={() => { setSearchTerm(''); clearFamilies(); }} className="px-4 py-3 text-gray-600 hover:text-gray-800">Clear</button>}
        </form>

        {/* Family Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-xl p-6">
                <div className="w-full h-20 bg-gray-200 rounded-xl animate-pulse mb-4"></div>
                <div className="space-y-2">{[1,2,3].map(j => <div key={j} className="w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>)}</div>
              </div>
            ))
          ) : families.length > 0 ? (
            families.map((family) => (
              <div key={family.id} className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all">
                {/* Family Head Header */}
                <div className="bg-gradient-to-br from-navy-700 to-navy-900 p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center"><Crown className="h-6 w-6 text-white" /></div>
                    <div className="text-white flex-1 min-w-0">
                      <h3 className="font-bold text-lg truncate">{family.familyHead?.fullName}</h3>
                      <p className="text-white text-opacity-80 text-sm">Family Head • {family.familyHead?.mrn}</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="mb-4 pb-4 border-b border-gray-100 grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center text-gray-600"><Shield className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" /><span className="truncate">{family.familyHead?.cnic || '—'}</span></div>
                    <div className="flex items-center text-gray-600"><Phone className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" /><span className="truncate">{family.familyHead?.phone || '—'}</span></div>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Members</span>
                      <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">{family.totalMembers} total</span>
                    </div>
                    <div className="space-y-2">
                      {(family.members || []).slice(0, 3).map((member, idx) => (
                        <div key={member.id || idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-white rounded-lg flex items-center justify-center shadow-sm">
                              {member.age < 18 ? <Baby className="h-4 w-4 text-amber-600" /> : member.relationship?.toLowerCase() === 'wife' ? <Heart className="h-4 w-4 text-pink-600" /> : <User className="h-4 w-4 text-blue-600" />}
                            </div>
                            <span className="text-sm font-medium text-gray-800">{member.fullName}</span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getRelationshipColor(member.relationship, member.age)}`}>
                            {member.relationship || (member.age < 18 ? 'Child' : 'Member')}
                          </span>
                        </div>
                      ))}
                      {(family.members || []).length > 3 && (
                        <div className="text-center text-sm text-gray-500">+{family.members.length - 3} more</div>
                      )}
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button onClick={() => setSelectedFamily(family)} className="flex-1 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl hover:from-emerald-700 hover:to-emerald-800 transition-all font-medium flex items-center justify-center space-x-2">
                      <Eye className="h-4 w-4" /><span>View</span>
                    </button>
                    {can('LINK_MEMBER') && (
                      <button onClick={() => openLinkModal(family.familyHead)} className="py-2.5 px-4 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all font-medium flex items-center justify-center" title="Add member">
                        <UserPlus className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : !searchPerformed ? (
            <div className="col-span-full bg-white rounded-2xl shadow-xl p-12 text-center border border-gray-100">
              <Search className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-500 mb-2">Search for a family</h3>
              <p className="text-gray-400 mb-6 max-w-lg mx-auto">Type a name, CNIC, MRN or phone number above and press Search. Families are listed only when they match a search, to keep patient details private.</p>
              {can('LINK_MEMBER') && (
                <button onClick={() => openLinkModal()} className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
                  <Link2 className="h-5 w-5" /><span>Link Family Members</span>
                </button>
              )}
            </div>
          ) : unlinkedPatients.length === 0 ? (
            <div className="col-span-full bg-white rounded-2xl shadow-xl p-12 text-center border border-gray-100">
              <Users className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-500 mb-2">No Results</h3>
              <p className="text-gray-400">No families or patients match "{searchTerm}".</p>
            </div>
          ) : null}
        </div>

        {/* Individual Patient Results (not in any family) */}
        {searchPerformed && unlinkedPatients.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center space-x-2 mb-4">
              <User className="h-5 w-5 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-700">Individual Patients</h2>
              <span className="text-sm text-gray-400">({unlinkedPatients.length} not in a family)</span>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="divide-y divide-gray-100">
                {unlinkedPatients.map((patient) => (
                  <div key={patient.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <div className="flex items-center space-x-4">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${patient.isAdult || (patient.age && patient.age >= 18) ? 'bg-blue-100' : 'bg-amber-100'}`}>
                        {patient.isAdult || (patient.age && patient.age >= 18) ? <User className="h-5 w-5 text-blue-600" /> : <Baby className="h-5 w-5 text-amber-600" />}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{getPatientName(patient)}</div>
                        <div className="text-sm text-gray-500 flex items-center space-x-3">
                          <span>{patient.mrn}</span>
                          <span>•</span>
                          <span>{patient.phoneNumber || patient.phone || '—'}</span>
                          <span>•</span>
                          <span>Age: {getPatientAge(patient)}</span>
                          {patient.area && <><span>•</span><span>{patient.area}</span></>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${patient.isAdult || (patient.age && patient.age >= 18) ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {patient.isAdult || (patient.age && patient.age >= 18) ? 'Adult' : 'Minor'}
                      </span>
                      {can('LINK_MEMBER') && (
                        <>
                          <button
                            onClick={() => openLinkModal(patient)}
                            className="flex items-center space-x-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                            title="Set as family head and add members"
                          >
                            <Crown className="h-3.5 w-3.5" /><span>Set as Head</span>
                          </button>
                          <button
                            onClick={() => openLinkModal()}
                            className="flex items-center space-x-1.5 px-3 py-1.5 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
                            title="Link as member of an existing family"
                          >
                            <Link2 className="h-3.5 w-3.5" /><span>Link to Family</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Family Details Modal */}
        {selectedFamily && (
          <div ref={familyDetailsDialogRef} className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) setSelectedFamily(null); }}>
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-br from-navy-700 to-navy-900 text-white p-8 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center"><Crown className="h-8 w-8 text-white" /></div>
                    <div>
                      <h1 className="text-2xl font-bold">{selectedFamily.familyHead?.fullName}</h1>
                      <p className="text-white text-opacity-80">Family Head • {selectedFamily.totalMembers} members</p>
                    </div>
                  </div>
                  <button aria-label="Close" onClick={() => setSelectedFamily(null)} className="p-3 hover:bg-white hover:bg-opacity-20 rounded-full"><X className="h-6 w-6" /></button>
                </div>
              </div>

              <div className="p-6">
                {/* Head info */}
                <div className="mb-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100 grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">MRN:</span> <span className="font-medium text-gray-800">{selectedFamily.familyHead?.mrn}</span></div>
                  <div><span className="text-gray-500">Phone:</span> <span className="font-medium text-gray-800">{selectedFamily.familyHead?.phone || '—'}</span></div>
                  <div><span className="text-gray-500">CNIC:</span> <span className="font-medium text-gray-800">{selectedFamily.familyHead?.cnic || '—'}</span></div>
                  <div><span className="text-gray-500">Age:</span> <span className="font-medium text-gray-800">{selectedFamily.familyHead?.age} years</span></div>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-800">Family Members</h3>
                  {can('LINK_MEMBER') && (
                    <button onClick={() => { setSelectedFamily(null); openLinkModal(selectedFamily.familyHead); }} className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
                      <UserPlus className="h-4 w-4" /><span>Add Member</span>
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  {(selectedFamily.members || []).length === 0 ? (
                    <div className="text-center py-6 text-gray-400">
                      <User className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                      <p>No members linked yet</p>
                    </div>
                  ) : (selectedFamily.members || []).map((member, idx) => (
                    <div key={member.id || idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${member.age < 18 ? 'bg-amber-100' : member.relationship?.toLowerCase() === 'wife' ? 'bg-pink-100' : 'bg-blue-100'}`}>
                          {member.age < 18 ? <Baby className="h-5 w-5 text-amber-600" /> : member.relationship?.toLowerCase() === 'wife' ? <Heart className="h-5 w-5 text-pink-600" /> : <User className="h-5 w-5 text-blue-600" />}
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">{member.fullName}</div>
                          <div className="text-sm text-gray-600">{member.mrn} • Age: {member.age}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRelationshipColor(member.relationship, member.age)}`}>
                          {member.relationship || (member.age < 18 ? 'Child' : 'Member')}
                        </span>
                        {can('UNLINK_MEMBER') && (
                          <button
                            onClick={() => setUnlinkConfirm({ familyHeadId: selectedFamily.id, memberId: member.id, memberName: member.fullName })}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Unlink member"
                          >
                            <Unlink className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="px-6 pb-6 flex justify-end">
                <button onClick={() => setSelectedFamily(null)} className="px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Link Family Members Modal */}
        {showLinkModal && (
          <div ref={linkDialogRef} className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) closeLinkModal(); }}>
            <div ref={linkModalBodyRef} className="bg-white rounded-3xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center"><Link2 className="h-6 w-6 text-white" /></div>
                    <div>
                      <h2 className="text-xl font-bold">Link Family Members</h2>
                      <p className="text-white text-opacity-80 text-sm">
                        {linkStep === 'search-head' && 'Step 1: Select the family head'}
                        {linkStep === 'search-member' && 'Step 2: Select a family member to link'}
                        {linkStep === 'confirm' && 'Step 3: Confirm the link'}
                      </p>
                    </div>
                  </div>
                  <button aria-label="Close" onClick={closeLinkModal} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full"><X className="h-5 w-5" /></button>
                </div>

                {/* Step indicators */}
                <div className="flex items-center mt-4 space-x-2">
                  {['Head', 'Member', 'Confirm'].map((step, i) => {
                    const stepKeys = ['search-head', 'search-member', 'confirm'];
                    const currentIdx = stepKeys.indexOf(linkStep);
                    const isActive = i === currentIdx;
                    const isDone = i < currentIdx;
                    return (
                      <React.Fragment key={step}>
                        <div className={`flex items-center space-x-1.5 px-3 py-1 rounded-full text-sm ${isActive ? 'bg-white text-blue-700 font-bold' : isDone ? 'bg-white bg-opacity-30 text-white' : 'bg-white bg-opacity-10 text-white text-opacity-60'}`}>
                          {isDone ? <Check className="h-3.5 w-3.5" /> : <span className="w-4 text-center">{i + 1}</span>}
                          <span>{step}</span>
                        </div>
                        {i < 2 && <ChevronRight className="h-4 w-4 text-white text-opacity-40" />}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              <div className="p-6">
                {/* Selected Head Display */}
                {selectedHead && linkStep !== 'search-head' && (
                  <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center"><Crown className="h-5 w-5 text-white" /></div>
                      <div>
                        <div className="font-bold text-gray-900 text-sm">{getPatientName(selectedHead)}</div>
                        <div className="text-xs text-gray-500">{selectedHead.mrn} • Age: {getPatientAge(selectedHead)}</div>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">Family Head</span>
                  </div>
                )}

                {/* Selected Member Display */}
                {selectedMember && linkStep === 'confirm' && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center"><User className="h-5 w-5 text-white" /></div>
                      <div>
                        <div className="font-bold text-gray-900 text-sm">{getPatientName(selectedMember)}</div>
                        <div className="text-xs text-gray-500">{selectedMember.mrn} • Age: {getPatientAge(selectedMember)}</div>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-blue-700 bg-blue-100 px-2 py-1 rounded-full">Member</span>
                  </div>
                )}

                {/* Search Input (for head or member steps) */}
                {(linkStep === 'search-head' || linkStep === 'search-member') && (
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder={linkStep === 'search-head' ? 'Search for family head (name, MRN, CNIC, phone)...' : 'Search for a member to add (name, MRN, CNIC, phone)...'}
                        value={patientSearch}
                        onChange={(e) => setPatientSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                      {searchingPatients && <div className="absolute right-3 top-1/2 transform -translate-y-1/2"><RefreshCw className="h-4 w-4 text-gray-400 animate-spin" /></div>}
                    </div>
                    {patientSearch.length > 0 && patientSearch.length < 2 && (
                      <p className="text-xs text-gray-400 mt-1">Type at least 2 characters to search</p>
                    )}
                  </div>
                )}

                {/* Patient Results */}
                {(linkStep === 'search-head' || linkStep === 'search-member') && patientResults.length > 0 && (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {patientResults
                      .filter(p => {
                        // When searching for member, exclude the selected head
                        if (linkStep === 'search-member' && selectedHead) return p.id !== selectedHead.id;
                        return true;
                      })
                      .map((patient) => (
                      <button
                        key={patient.id}
                        onClick={() => linkStep === 'search-head' ? selectAsHead(patient) : selectAsMember(patient)}
                        className="w-full p-3 bg-gray-50 hover:bg-blue-50 rounded-xl flex items-center justify-between transition-colors text-left border border-transparent hover:border-blue-200"
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${patient.isAdult || (patient.age && patient.age >= 18) ? 'bg-blue-100' : 'bg-amber-100'}`}>
                            {patient.isAdult || (patient.age && patient.age >= 18) ? <User className="h-5 w-5 text-blue-600" /> : <Baby className="h-5 w-5 text-amber-600" />}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{getPatientName(patient)}</div>
                            <div className="text-xs text-gray-500">{patient.mrn} • {patient.phoneNumber || patient.phone || '—'} • Age: {getPatientAge(patient)}</div>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                )}

                {/* No results */}
                {(linkStep === 'search-head' || linkStep === 'search-member') && patientSearch.length >= 2 && !searchingPatients && patientResults.length === 0 && (
                  <div className="text-center py-6 text-gray-400">
                    <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>{patientSearchHint || `No patients found for "${patientSearch}"`}</p>
                  </div>
                )}

                {/* Confirm Step */}
                {linkStep === 'confirm' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <p className="text-gray-700 text-center mb-4">
                      Link <strong>{getPatientName(selectedMember)}</strong> as a member of <strong>{getPatientName(selectedHead)}</strong>'s family?
                    </p>
                    <div className="flex space-x-3">
                      <button onClick={() => { setLinkStep('search-member'); setSelectedMember(null); setPatientSearch(''); setPatientResults([]); }} className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 font-medium">
                        Back
                      </button>
                      <button onClick={confirmLink} disabled={linkingInProgress} className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium disabled:opacity-50 flex items-center justify-center space-x-2">
                        {linkingInProgress ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                        <span>{linkingInProgress ? 'Linking...' : 'Confirm Link'}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Unlink Confirmation Modal */}
        {unlinkConfirm && (
          <div ref={unlinkDialogRef} className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4" onClick={(e) => { if (e.target === e.currentTarget) setUnlinkConfirm(null); }}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center"><Unlink className="h-6 w-6 text-red-600" /></div>
                <div>
                  <h3 className="font-bold text-gray-900">Unlink Member</h3>
                  <p className="text-sm text-gray-500">This will remove the family relationship</p>
                </div>
              </div>
              <p className="text-gray-700 mb-6">Are you sure you want to unlink <strong>{unlinkConfirm.memberName}</strong> from this family?</p>
              <div className="flex space-x-3">
                <button onClick={() => setUnlinkConfirm(null)} className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 font-medium">Cancel</button>
                <button onClick={confirmUnlink} className="flex-1 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium">Unlink</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FamilyManagement;
